import { Component, ElementRef, ViewChild } from '@angular/core';

declare const bootstrap: any;

export interface Program {

  title: string;

  years: string;

  href: string;

  applyHref: string;

  shareOpen?: boolean;

}

export interface ProgramCategory {

  id: string;

  label: string;

  group: 'programs' | 'schools';

  programs: Program[];

  externalHref?: string;

}

const APPLY_URL = 'https://admissions.ghru.edu.in/';
// All the "in collaboration with IBM" programs share one page on raisoni.net
const IBM_URL = 'https://raisoni.net/ibm';

@Component({
  selector: 'app-academic-navigation',
  templateUrl: './academic-navigation.component.html',
  styleUrls: ['./academic-navigation.component.scss']
})
export class AcademicNavigationComponent {
  activeCategoryId = 'diploma';
  menuOpened = false;
  expandedMobileId: string | null = null; // tracks which accordion row is open on mobile


  get activeCategory(): ProgramCategory | undefined {
    return this.categories.find(c => c.id === this.activeCategoryId);
  }

  get programsOfferedCategories(): ProgramCategory[] {
    return this.categories.filter(c => c.group === 'programs');
  }

  get schoolCategories(): ProgramCategory[] {
    return this.categories.filter(c => c.group === 'schools');
  }

  onCategoryClick(id: string): void {
    this.activeCategoryId = id;
  }

  // Accordion toggle: clicking an open row closes it; clicking a closed
  // row opens it and closes whatever else was open (single-open accordion).
  toggleMobileCategory(id: string): void {
    this.expandedMobileId = this.expandedMobileId === id ? null : id;
  }

  @ViewChild('menuDropdown') private menuDropdown?: ElementRef<HTMLElement>;

  // The menu uses data-bs-auto-close="outside", so it stays open on inner
  // clicks; close it ourselves after navigating to a course page.
  closeMenu(): void {
    const toggle = this.menuDropdown?.nativeElement.querySelector('[data-bs-toggle="dropdown"]');
    if (toggle && typeof bootstrap !== 'undefined') {
      bootstrap.Dropdown.getOrCreateInstance(toggle).hide();
    }
  }

  isExternal(href: string): boolean {
    return /^https?:/.test(href);
  }

  shareUrl(program: Program): string {
    return program.href.startsWith('/') ? window.location.origin + program.href : window.location.href;
  }

  // Course names and slugs mirror the live ghru.edu.in mega menu. Slugs are
  // root-relative, so they resolve against whichever host serves the app.
  categories: ProgramCategory[] = [
    {
      id: 'diploma',
      label: 'Diploma Programs',
      group: 'programs',
      programs: [
        { title: 'Diploma Computer Science Engineering', years: '3 Years', href: '/diploma-computer-science-engg', applyHref: APPLY_URL },
        { title: 'Diploma Mechanical Engineering', years: '3 Years', href: '/diploma-mechanical-engg', applyHref: APPLY_URL },
        { title: 'Diploma Civil Engineering', years: '3 Years', href: '/diploma-civil-engg', applyHref: APPLY_URL },
        { title: 'Diploma Mining Engineering', years: '3 Years', href: '/diploma-mining-engg', applyHref: APPLY_URL },
        { title: 'D.Pharm', years: '2 Years', href: '/diploma-pharmacy', applyHref: APPLY_URL },
        { title: 'Diploma in Medical Laboratory Technology (DMLT)', years: '2 Years', href: '/dmlt', applyHref: APPLY_URL },
        { title: 'Diploma in Agriculture', years: '3 Years', href: '/diploma-agriculture', applyHref: APPLY_URL },
      ]
    },
    {
      id: 'ug',
      label: 'Under Graduate Programs',
      group: 'programs',
      programs: [
        { title: 'B.Tech Computer Science & Engineering', years: '4 Years', href: '/btech-computer-science-engg', applyHref: APPLY_URL },
        { title: 'B.Tech Robotics and Artificial Intelligence', years: '4 Years', href: '/btech-artificial-intelligence', applyHref: APPLY_URL },
        { title: 'B.Tech Civil Engineering', years: '4 Years', href: '/btech-in-civil-engg', applyHref: APPLY_URL },
        { title: 'B.Tech Mechanical Engineering', years: '4 Years', href: '/btech-mechanical-engg', applyHref: APPLY_URL },
        { title: 'B.Tech Mining Engineering', years: '4 Years', href: '/btech-mining-engg', applyHref: APPLY_URL },
        { title: 'B.Tech in Data Science', years: '4 Years', href: '/btech-data-science', applyHref: APPLY_URL },
        { title: 'B.Tech in CSE (Cyber Security)', years: '4 Years', href: '/btech-data-science-internet-cyber-security', applyHref: APPLY_URL },
        { title: 'B.Tech in CSE (Artificial Intelligence & Machine Learning)', years: '4 Years', href: '/btech-artificial-intelligence-machine-learning', applyHref: APPLY_URL },
        { title: 'B.Tech in Electronics and Telecommunication Engineering', years: '4 Years', href: '/btech-electronics-communication-engineering', applyHref: APPLY_URL },
        { title: 'B.Sc in Agriculture', years: '4 Years', href: '/bsc-agriculture', applyHref: APPLY_URL },
        { title: 'B.Pharm (1st Year)', years: '4 Years', href: '/bachelor-of-pharmacy', applyHref: APPLY_URL },
        { title: 'B.Pharm (Direct 2nd Year)', years: '3 Years', href: '/bachelor-of-pharmacy-dy-2year', applyHref: APPLY_URL },
        { title: 'BMLT (Bachelor of Medical Laboratory Technology)', years: '3 Years', href: '/bmlt', applyHref: APPLY_URL },
        { title: 'B.Sc in Information Technology', years: '3 Years', href: '/bsc-it', applyHref: APPLY_URL },
        { title: 'B.Sc in PCM (Physics, Chemistry, Mathematics)', years: '3 Years', href: '/bsc-pcm', applyHref: APPLY_URL },
        { title: 'B.Sc in CBZ (Chemistry, Botany, Zoology)', years: '3 Years', href: '/bsc-cbz', applyHref: APPLY_URL },
        { title: 'B.Sc in Cyber Security', years: '4 Years', href: '/bsc-cyber-security', applyHref: APPLY_URL },
        { title: 'B.Sc in Biotechnology', years: '3 Years', href: '/bsc-biotechnology', applyHref: APPLY_URL },
        { title: 'BCA', years: '3 Years', href: '/bca', applyHref: APPLY_URL },
        { title: 'B.Sc Nursing', years: '4 Years', href: '/bsc-nursing', applyHref: APPLY_URL },
        { title: 'BA LL.B. (5 Years)', years: '5 Years', href: '/ba-llb', applyHref: APPLY_URL },
        { title: 'BBA LL.B. (5 Years)', years: '5 Years', href: '/bba-llb', applyHref: APPLY_URL },
        { title: 'B.Com LL.B. (5 Years)', years: '5 Years', href: '/bcom-llb', applyHref: APPLY_URL },
        { title: 'B.Sc LL.B. (5 Years)', years: '5 Years', href: '/bsc-llb', applyHref: APPLY_URL },
        { title: 'LL.B. (3 Years)', years: '3 Years', href: '/llb-3year', applyHref: APPLY_URL },
        { title: 'BBA (General)', years: '3 Years', href: '/bba-general', applyHref: APPLY_URL },
        { title: 'B.Com (General)', years: '3 Years', href: '/bcom-general', applyHref: APPLY_URL },
        { title: 'B.Tech In CSE AI & Machine Learning In Collaboration With IBM', years: '4 Years', href: IBM_URL, applyHref: APPLY_URL },
        { title: 'B.Tech In CSE Advanced & Modern AI In Collaboration With IBM', years: '4 Years', href: IBM_URL, applyHref: APPLY_URL },
        { title: 'B.Tech In CSE Cyb Sec & Forensics In Collaboration With IBM', years: '4 Years', href: IBM_URL, applyHref: APPLY_URL },
        { title: 'B.Tech In CSE DS With AI & ML In Collaboration With IBM', years: '4 Years', href: IBM_URL, applyHref: APPLY_URL },
        { title: 'B.Tech In Agriculture In Collaboration With IBM', years: '4 Years', href: IBM_URL, applyHref: APPLY_URL },
        { title: 'BA - Civil Services, Journalism , Mass Communication', years: '4 Years', href: '/ba-journalism', applyHref: APPLY_URL },
        { title: 'BBA (International Business)', years: '3 Years', href: '/bba-specialization', applyHref: APPLY_URL },
        { title: 'B.Tech. Agriculture', years: '4 Years', href: '/btech-agriculture', applyHref: APPLY_URL },
        { title: 'B.Sc Data Science', years: '3 Years', href: '/bsc-it-data-science', applyHref: APPLY_URL },
        { title: 'B.Sc Computer Science', years: '3 Years', href: '/bsc-computer-science', applyHref: APPLY_URL },
        { title: 'B.tech Quantum Computing', years: '4 Years', href: '/btech-quantum-computing', applyHref: APPLY_URL },
      ]
    },
    {
      id: 'pg',
      label: 'Post Graduate Programs',
      group: 'programs',
      programs: [
        { title: 'M.Tech Structural Engineering', years: '2 Years', href: '/mtech-structural-engg', applyHref: APPLY_URL },
        { title: 'M.Tech Transportation Engineering', years: '2 Years', href: '/mtech-transportation-engg', applyHref: APPLY_URL },
        { title: 'M.Tech Mining Engineering', years: '2 Years', href: '/mtech-mining-engg', applyHref: APPLY_URL },
        { title: 'M.Tech Computer Science & Engineering', years: '2 Years', href: '/mtech-computer-science-engg', applyHref: APPLY_URL },
        { title: 'M.Tech in Data Science', years: '2 Years', href: '/mtech-data-science-engg', applyHref: APPLY_URL },
        { title: 'M.Tech in Artificial Intelligence', years: '2 Years', href: '/mtech-artificial-intelligence', applyHref: APPLY_URL },
        { title: 'M.Tech in CAD/CAM', years: '2 Years', href: '/mtech-cad-cam', applyHref: APPLY_URL },
        { title: 'M.Tech in Electrical Power System', years: '2 Years', href: '/mtech-electrical-power-system', applyHref: APPLY_URL },
        { title: 'M.Tech in Construction Technology Management', years: '2 Years', href: '/mtech-construction-engg-management', applyHref: APPLY_URL },
        { title: 'M.Sc Agriculture – Agronomy', years: '2 Years', href: '/msc-agri-agronomy', applyHref: APPLY_URL },
        { title: 'M.Sc Agriculture – Horticulture (Fruit Science)', years: '2 Years', href: '/msc-agri-horticulture', applyHref: APPLY_URL },
        { title: 'M.Sc Agriculture – Horticulture (Vegetable Science)', years: '2 Years', href: '/msc-agri-horticulture', applyHref: APPLY_URL },
        { title: 'M.Sc Agriculture – Entomology', years: '2 Years', href: '/msc-agri-entomology', applyHref: APPLY_URL },
        { title: 'M.Sc Agriculture – Plant Pathology', years: '2 Years', href: '/msc-agri-plant-pathology', applyHref: APPLY_URL },
        { title: 'M.Sc Agriculture – Extension Education & Communication', years: '2 Years', href: '/msc-agri-extension-communication', applyHref: APPLY_URL },
        { title: 'M.Pharm in Pharmaceutics', years: '2 Years', href: '/mpharm', applyHref: APPLY_URL },
        { title: 'M.Pharm in Quality Assurance', years: '2 Years', href: '/mpharm', applyHref: APPLY_URL },
        { title: 'M.Sc in Physics', years: '2 Years', href: '/msc-physics', applyHref: APPLY_URL },
        { title: 'M.Sc in Mathematics', years: '2 Years', href: '/msc-mathematics', applyHref: APPLY_URL },
        { title: 'M.Sc in Chemistry', years: '2 Years', href: '/msc-chemistry', applyHref: APPLY_URL },
        { title: 'M.Sc in Zoology', years: '2 Years', href: '/msc-zoology', applyHref: APPLY_URL },
        { title: 'M.Sc in Biotechnology', years: '2 Years', href: '/msc-biotechnology', applyHref: APPLY_URL },
        { title: 'MBA (General)', years: '2 Years', href: '/mba-general', applyHref: APPLY_URL },
        { title: 'MBA in Agri Business Management', years: '2 Years', href: '/mba-agri-business-management', applyHref: APPLY_URL },
        { title: 'M.Com', years: '2 Years', href: '/master-of-commerce', applyHref: APPLY_URL },
        { title: 'LL.M. (1 Year)', years: '1 Year', href: '/pg-llm-1year', applyHref: APPLY_URL },
      ]
    },
    {
      id: 'research',
      label: 'Research Programs',
      group: 'programs',
      programs: [
        { title: 'Ph.D Mechanical Engineering', years: '4+ Years', href: '/phd-mechanical', applyHref: APPLY_URL },
        { title: 'Ph.D Computer Science & Engineering', years: '4+ Years', href: '/phd-cse', applyHref: APPLY_URL },
        { title: 'Ph.D Civil Engineering', years: '4+ Years', href: '/phd-civil', applyHref: APPLY_URL },
        { title: 'Ph.D in Physics', years: '4+ Years', href: '/phd-physics', applyHref: APPLY_URL },
        { title: 'Ph.D in Mathematics', years: '4+ Years', href: '/phd-maths', applyHref: APPLY_URL },
        { title: 'Ph.D in Chemistry', years: '4+ Years', href: '/phd-chemistry', applyHref: APPLY_URL },
        { title: 'Ph.D in Management', years: '4+ Years', href: '/phd-management', applyHref: APPLY_URL },
      ]
    },
    {
      id: 'engg',
      label: 'School of Engineering & Technology',
      group: 'schools',
      programs: [
        { title: 'Diploma Computer Science Engineering', years: '3 Years', href: '/diploma-computer-science-engg', applyHref: APPLY_URL },
        { title: 'Diploma Mechanical Engineering', years: '3 Years', href: '/diploma-mechanical-engg', applyHref: APPLY_URL },
        { title: 'Diploma Civil Engineering', years: '3 Years', href: '/diploma-civil-engg', applyHref: APPLY_URL },
        { title: 'Diploma Mining Engineering', years: '3 Years', href: '/diploma-mining-engg', applyHref: APPLY_URL },
        { title: 'B.Tech Computer Science & Engineering', years: '4 Years', href: '/btech-computer-science-engg', applyHref: APPLY_URL },
        { title: 'B.Tech Robotics and Artificial Intelligence', years: '4 Years', href: '/btech-artificial-intelligence', applyHref: APPLY_URL },
        { title: 'B.Tech Civil Engineering', years: '4 Years', href: '/btech-in-civil-engg', applyHref: APPLY_URL },
        { title: 'B.Tech Mechanical Engineering', years: '4 Years', href: '/btech-mechanical-engg', applyHref: APPLY_URL },
        { title: 'B.Tech Mining Engineering', years: '4 Years', href: '/btech-mining-engg', applyHref: APPLY_URL },
        { title: 'B.Tech in Data Science', years: '4 Years', href: '/btech-data-science', applyHref: APPLY_URL },
        { title: 'B.Tech in CSE (Cyber Security)', years: '4 Years', href: '/btech-data-science-internet-cyber-security', applyHref: APPLY_URL },
        { title: 'B.Tech in CSE (Artificial Intelligence & Machine Learning)', years: '4 Years', href: '/btech-artificial-intelligence-machine-learning', applyHref: APPLY_URL },
        { title: 'B.Tech in Electronics and Telecommunication Engineering', years: '4 Years', href: '/btech-electronics-communication-engineering', applyHref: APPLY_URL },
        { title: 'M.Tech Structural Engineering', years: '2 Years', href: '/mtech-structural-engg', applyHref: APPLY_URL },
        { title: 'M.Tech Transportation Engineering', years: '2 Years', href: '/mtech-transportation-engg', applyHref: APPLY_URL },
        { title: 'M.Tech Mining Engineering', years: '2 Years', href: '/mtech-mining-engg', applyHref: APPLY_URL },
        { title: 'M.Tech Computer Science & Engineering', years: '2 Years', href: '/mtech-computer-science-engg', applyHref: APPLY_URL },
        { title: 'M.Tech in Data Science', years: '2 Years', href: '/mtech-data-science-engg', applyHref: APPLY_URL },
        { title: 'M.Tech in Artificial Intelligence', years: '2 Years', href: '/mtech-artificial-intelligence', applyHref: APPLY_URL },
        { title: 'M.Tech in CAD/CAM', years: '2 Years', href: '/mtech-cad-cam', applyHref: APPLY_URL },
        { title: 'M.Tech in Electrical Power System', years: '2 Years', href: '/mtech-electrical-power-system', applyHref: APPLY_URL },
        { title: 'M.Tech in Construction Technology Management', years: '2 Years', href: '/mtech-construction-engg-management', applyHref: APPLY_URL },
        { title: 'Ph.D Mechanical Engineering', years: '4+ Years', href: '/phd-mechanical', applyHref: APPLY_URL },
        { title: 'Ph.D Computer Science & Engineering', years: '4+ Years', href: '/phd-cse', applyHref: APPLY_URL },
        { title: 'Ph.D Civil Engineering', years: '4+ Years', href: '/phd-civil', applyHref: APPLY_URL },
        { title: 'B.tech Quantum Computing', years: '4 Years', href: '/btech-quantum-computing', applyHref: APPLY_URL },
      ]
    },
    {
      id: 'law',
      label: 'School of Law',
      group: 'schools',
      programs: [
        { title: 'BA LL.B. (5 Years)', years: '5 Years', href: '/ba-llb', applyHref: APPLY_URL },
        { title: 'BBA LL.B. (5 Years)', years: '5 Years', href: '/bba-llb', applyHref: APPLY_URL },
        { title: 'B.Com LL.B. (5 Years)', years: '5 Years', href: '/bcom-llb', applyHref: APPLY_URL },
        { title: 'B.Sc LL.B. (5 Years)', years: '5 Years', href: '/bsc-llb', applyHref: APPLY_URL },
        { title: 'LL.B. (3 Years)', years: '3 Years', href: '/llb-3year', applyHref: APPLY_URL },
        { title: 'LL.M. (1 Year)', years: '1 Year', href: '/pg-llm-1year', applyHref: APPLY_URL },
      ]
    },
    {
      id: 'pharmacy',
      label: 'School of Pharmacy',
      group: 'schools',
      programs: [
        { title: 'D.Pharm', years: '2 Years', href: '/diploma-pharmacy', applyHref: APPLY_URL },
        { title: 'B.Pharm (1st Year)', years: '4 Years', href: '/bachelor-of-pharmacy', applyHref: APPLY_URL },
        { title: 'B.Pharm (Direct 2nd Year)', years: '3 Years', href: '/bachelor-of-pharmacy-dy-2year', applyHref: APPLY_URL },
        { title: 'M.Pharm in Pharmaceutics', years: '2 Years', href: '/mpharm', applyHref: APPLY_URL },
        { title: 'M.Pharm in Quality Assurance', years: '2 Years', href: '/mpharm', applyHref: APPLY_URL },
      ]
    },
    {
      id: 'cs',
      label: 'School of Sciences',
      group: 'schools',
      programs: [
        { title: 'B.Sc in Information Technology', years: '3 Years', href: '/bsc-it', applyHref: APPLY_URL },
        { title: 'B.Sc in CBZ (Chemistry, Botany, Zoology)', years: '3 Years', href: '/bsc-cbz', applyHref: APPLY_URL },
        { title: 'B.Sc in Biotechnology', years: '3 Years', href: '/bsc-biotechnology', applyHref: APPLY_URL },
        { title: 'BCA', years: '3 Years', href: '/bca', applyHref: APPLY_URL },
        { title: 'M.Sc in Physics', years: '2 Years', href: '/msc-physics', applyHref: APPLY_URL },
        { title: 'M.Sc in Mathematics', years: '2 Years', href: '/msc-mathematics', applyHref: APPLY_URL },
        { title: 'M.Sc in Chemistry', years: '2 Years', href: '/msc-chemistry', applyHref: APPLY_URL },
        { title: 'M.Sc in Zoology', years: '2 Years', href: '/msc-zoology', applyHref: APPLY_URL },
        { title: 'M.Sc in Biotechnology', years: '2 Years', href: '/msc-biotechnology', applyHref: APPLY_URL },
        { title: 'Ph.D in Physics', years: '4+ Years', href: '/phd-physics', applyHref: APPLY_URL },
        { title: 'Ph.D in Mathematics', years: '4+ Years', href: '/phd-maths', applyHref: APPLY_URL },
        { title: 'Ph.D in Chemistry', years: '4+ Years', href: '/phd-chemistry', applyHref: APPLY_URL },
        { title: 'B.Sc Data Science', years: '3 Years', href: '/bsc-it-data-science', applyHref: APPLY_URL },
        { title: 'B.Sc Computer Science', years: '3 Years', href: '/bsc-computer-science', applyHref: APPLY_URL },
      ]
    },
    {
      id: 'agricultural',
      label: 'School of Agricultural',
      group: 'schools',
      programs: [
        { title: 'Diploma in Agriculture', years: '3 Years', href: '/diploma-agriculture', applyHref: APPLY_URL },
        { title: 'B.Sc in Agriculture', years: '4 Years', href: '/bsc-agriculture', applyHref: APPLY_URL },
        { title: 'B.Tech. Agriculture', years: '4 Years', href: '/btech-agriculture', applyHref: APPLY_URL },
        { title: 'M.Sc Agriculture – Agronomy', years: '2 Years', href: '/msc-agri-agronomy', applyHref: APPLY_URL },
        { title: 'M.Sc Agriculture – Horticulture (Fruit Science)', years: '2 Years', href: '/msc-agri-horticulture', applyHref: APPLY_URL },
        { title: 'M.Sc Agriculture – Horticulture (Vegetable Science)', years: '2 Years', href: '/msc-agri-horticulture', applyHref: APPLY_URL },
        { title: 'M.Sc Agriculture – Entomology', years: '2 Years', href: '/msc-agri-entomology', applyHref: APPLY_URL },
        { title: 'M.Sc Agriculture – Plant Pathology', years: '2 Years', href: '/msc-agri-plant-pathology', applyHref: APPLY_URL },
        // Genetics and Ph.D have no course page yet, so they open the school page
        { title: 'M.Sc Agriculture – Genetics & Plant Breeding', years: '2 Years', href: '/schools/agricultural', applyHref: APPLY_URL },
        { title: 'M.Sc Agriculture – Extension Education & Communication', years: '2 Years', href: '/msc-agri-extension-communication', applyHref: APPLY_URL },
        { title: 'MBA in Agri Business Management', years: '2 Years', href: '/mba-agri-business-management', applyHref: APPLY_URL },
        { title: 'Ph.D in Agriculture Science', years: '4 Years', href: '/schools/agricultural', applyHref: APPLY_URL },
      ]
    },
    {
      id: 'paramedical',
      label: 'School of Paramedical Courses',
      group: 'schools',
      programs: [
        // No dedicated course page yet, so the school page carries the program details
        { title: 'Diploma in X-Ray Technology', years: '1 Year', href: '/schools/diploma-x-ray-technician', applyHref: APPLY_URL },
      ]
    },
    {
      id: 'nursing',
      label: 'School of Nursing',
      group: 'schools',
      programs: [
        { title: 'B.Sc Nursing', years: '4 Years', href: '/bsc-nursing', applyHref: APPLY_URL },
      ]
    },
    {
      id: 'arts',
      label: 'School of Arts',
      group: 'schools',
      programs: [
        { title: 'BA - Civil Services, Journalism , Mass Communication', years: '4 Years', href: '/ba-journalism', applyHref: APPLY_URL },
      ]
    },
    {
      id: 'mng',
      label: 'School of Commerce and Management',
      group: 'schools',
      programs: [
        { title: 'BBA (General)', years: '3 Years', href: '/bba-general', applyHref: APPLY_URL },
        { title: 'B.Com (General)', years: '3 Years', href: '/bcom-general', applyHref: APPLY_URL },
        { title: 'MBA (General)', years: '2 Years', href: '/mba-general', applyHref: APPLY_URL },
        { title: 'M.Com', years: '2 Years', href: '/master-of-commerce', applyHref: APPLY_URL },
        { title: 'Ph.D in Management', years: '4+ Years', href: '/phd-management', applyHref: APPLY_URL },
        { title: 'BBA (International Business)', years: '3 Years', href: '/bba-specialization', applyHref: APPLY_URL },
        { title: 'MBA Agri Business Management', years: '2 Years', href: '/mba-agri-business-management', applyHref: APPLY_URL },
      ]
    }
  ];

}
